import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone, Mail, MapPin } from "lucide-react";

const LOGO_URL = "/images/logo.png";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export const Footer = () => {
  return (
    <footer className="bg-kairos-black border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" data-testid="footer-logo">
              <img
                src={LOGO_URL}
                alt="Kairos La Barberia"
                className="h-24 w-auto mb-6"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed font-sans">
              Il momento giusto per prenderti cura di te.
            </p>
            <p className="text-white/30 text-xs mt-4 font-accent tracking-wider">
              Est. 2023
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-kairos-gold text-xs uppercase tracking-[0.2em] font-bold mb-6">
              Navigazione
            </h4>
            <ul className="space-y-3">
              {["Home", "Servizi", "Chi Sono", "Galleria", "Contatti"].map((item) => (
                <li key={item}>
                  <Link
                    to={item === "Home" ? "/" : item === "Chi Sono" ? "/chi-siamo" : `/${item.toLowerCase().replace(" ", "-")}`}
                    className="text-white/60 hover:text-kairos-gold transition-colors text-sm"
                    data-testid={`footer-link-${item.toLowerCase().replace(" ", "-")}`}
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-kairos-gold text-xs uppercase tracking-[0.2em] font-bold mb-6">
              Contatti
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+393517252089"
                  className="flex items-center gap-3 text-white/60 hover:text-kairos-gold transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-kairos-gold" />
                  +39 351 725 2089
                </a>
              </li>
              <li>
                <a
                  href="mailto:kairoslabarberia@outlook.it"
                  className="flex items-center gap-3 text-white/60 hover:text-kairos-gold transition-colors text-sm"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-kairos-gold" />
                  kairoslabarberia@outlook.it
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60 text-sm">
                  <MapPin className="w-4 h-4 text-kairos-gold mt-0.5" />
                  <span>Desenzano del Garda, BS</span>
                </div>
              </li>
            </ul>
          </div>

          {/* App Download */}
          <div>
            <h4 className="text-kairos-gold text-xs uppercase tracking-[0.2em] font-bold mb-6">
              Prenota con l'App
            </h4>
            <div className="space-y-3">
              <a
                href="https://apps.apple.com/it/app/kairos-la-barberia/id6449093373"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-kairos-gray/50 border border-white/10 px-4 py-3 hover:border-kairos-gold/50 transition-colors"
                data-testid="footer-app-apple"
              >
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span className="text-white/80 text-sm">App Store</span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=app.kairos.working&hl=it"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-kairos-gray/50 border border-white/10 px-4 py-3 hover:border-kairos-gold/50 transition-colors"
                data-testid="footer-app-android"
              >
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14.2 8.5c.6.36.6 1.24 0 1.6l-14.2 8.5c-.66.5-1.6.03-1.6-.8z"/>
                </svg>
                <span className="text-white/80 text-sm">Google Play</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} Kairos La Barberia. Tutti i diritti riservati.
          </p>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/p/Kairos-La-Barberia-100092947623058/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-kairos-gold transition-colors"
              data-testid="footer-social-facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://www.instagram.com/kairos_la_barberia/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-kairos-gold transition-colors"
              data-testid="footer-social-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.tiktok.com/@kairoslabarberia"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-kairos-gold transition-colors"
              data-testid="footer-social-tiktok"
            >
              <TikTokIcon />
            </a>
          </div>

          <div className="flex gap-6">
            <Link
              to="/privacy-policy"
              className="text-white/30 hover:text-white/50 text-xs transition-colors"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-white/30 hover:text-white/50 text-xs transition-colors"
              data-testid="footer-terms"
            >
              Termini e Condizioni
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
